"use client"
import { useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"

// Set by AuthProvider when in local dev mode (no Auth0)
export let __localDevMode = false
import { User } from "@auth0/auth0-spa-js"

export function setLocalDevMode(enabled: boolean): void {
  __localDevMode = enabled
}

export const GROUP_KEY = "https://malariaatlas.org/groups"

// Module-level so local dev mode returns the same function on every render,
// matching the referential stability of the Auth0 getAccessToken below.
const getLocalDevAccessToken = (): Promise<string> => Promise.resolve("local-dev-token")

/**
 * Determines whether an error from getAccessTokenSilently indicates that the
 * user's session is unrecoverable and requires re-authentication.
 *
 * Covers the known Auth0 SDK error messages for expired, revoked, or rotated
 * refresh tokens that can no longer be exchanged for a new access token.
 *
 * @param error - The error thrown by the Auth0 SDK.
 * @returns true if the error represents an expired or invalid refresh token.
 */
function isUnrecoverableAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const message = error.message.toLowerCase()
  return (
    message.includes("login_required") ||
    message.includes("invalid_grant") ||
    message.includes("missing refresh token") ||
    message.includes("unknown or invalid refresh token")
  )
}

/**
 * Hook providing a unified authentication interface for the application.
 * Wraps Auth0's useAuth0 hook and adds graceful error handling for refresh
 * token failures — if the token is irrecoverably invalid, the user is
 * redirected to re-authenticate rather than seeing a cryptic error.
 *
 * The returned getAccessToken function is memoized with useCallback so it
 * maintains a stable reference across renders, making it safe to use in
 * useEffect dependency arrays.
 *
 * @returns An object containing auth state and methods:
 *   - isAuthenticated: boolean
 *   - user: Auth0 user object or undefined
 *   - signIn: triggers login redirect
 *   - signOut: triggers logout
 *   - isLoading: boolean
 *   - error: Auth0 error or undefined
 *   - token: null (unused, kept for interface compatibility)
 *   - getAccessToken: async function returning an access token string
 */
export function useAuth() {
  // Hooks run unconditionally (rules of hooks). Outside an Auth0Provider, as in
  // local dev mode, useAuth0 returns stub methods that only throw when called.
  const auth = useAuth0()
  const { getAccessTokenSilently, loginWithRedirect } = auth

  /**
   * Retrieves an access token silently using the cached refresh token.
   * If the refresh token is invalid or expired (e.g. due to rotation conflicts
   * across multiple tabs), redirects the user to re-authenticate.
   *
   * Wrapped in useCallback to maintain referential stability — components that
   * include this in useEffect dependency arrays will not re-run on every render.
   *
   * @returns The access token string.
   * @throws Redirects to login and throws if the refresh token is irrecoverable.
   * @throws Re-throws errors that are not related to token expiry/invalidity.
   */
  const getAccessToken = useCallback(async (): Promise<string> => {
    try {
      return await getAccessTokenSilently()
    } catch (error: unknown) {
      if (isUnrecoverableAuthError(error)) {
        // Refresh token is irrecoverably invalid — send user through login flow.
        // loginWithRedirect navigates away, so the throw below is unreachable in
        // practice but satisfies the return type contract for TypeScript.
        await loginWithRedirect()
        throw new Error("Redirecting to login due to expired session.")
      }
      throw error
    }
  }, [getAccessTokenSilently, loginWithRedirect])

  if (__localDevMode) {
    return {
      isAuthenticated: true,
      user: null,
      signIn: () => {},
      signOut: () => {},
      isLoading: false,
      error: null,
      token: null,
      getAccessToken: getLocalDevAccessToken,
    }
  }

  return {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    signIn: () => auth.loginWithRedirect(),
    signOut: () => auth.logout({ logoutParams: { returnTo: window.location.origin } }),
    isLoading: auth.isLoading,
    error: auth.error,
    token: null,
    getAccessToken,
  }
}

export function isUserInMapAppsAdmins(user: User | undefined | null) {
  const userGroups = user?.[GROUP_KEY]
  return userGroups?.includes("map-apps-admins")
}
